/**
 * PhaseBridge renders a vertical navigational progress bar.
 * It visualizes the current journey by mapping through the phase manifest
 * and projecting the completion status of each node.
 */

import { PhaseState } from "../../utils/Interfaces"

export const PhaseBridge = ({ phaseState }: { phaseState: PhaseState }) => {

    const { manifest, current } = phaseState;

    // Helper to determine the status of a phase relative to the current sequence
    const getPhaseStatus = (sequence: number) => {
        if (!current) return 'pending';
        if (current.sequence === sequence) return 'active';
        if (current.sequence > sequence) return 'completed';
        return 'pending';
    };

    return (
        <ul className="hidden md:flex flex-col w-full pt-6">
            {
                manifest.map((phase, index) => {
                    const status = getPhaseStatus(phase.sequence);
                    const isLast = index === manifest.length - 1;

                    return (
                        <li key={phase.slug} className="flex gap-x-4 group">
                            {/* Visual Column (Icon & Line) */}
                            <div className="flex flex-col items-center">
                                <div className="relative flex items-center justify-center w-8 h-8">
                                    <PhaseIndicator status={status} />
                                </div>

                                {/* Interconnecting Line */}
                                {
                                    !isLast && (
                                        <div className={`w-px flex-grow my-1 transition-colors duration-300 ${status === 'completed' ? "bg-orange-500" : "bg-stone-300 dark:bg-stone-700"}`} />
                                    )
                                }
                            </div>

                            {/* Label Column */}
                            <div className={`pb-8 pt-1 transition-colors duration-300 ${status === 'active' ? "text-orange-600 dark:text-orange-500 font-medium" : "text-stone-500 dark:text-stone-400"}`}>
                                <p className="text-sm lg:text-base leading-none">
                                    {phase.title}
                                </p>
                            </div>
                        </li>
                    );
                })
            }
        </ul>
    );
}

/**
 * Sub-component for the SVG icons to reduce nesting depth
 */
const PhaseIndicator = ({ status }: { status: 'completed' | 'active' | 'pending' }) => {
    const baseClass = "transition-all duration-300";

    if (status === 'active') {
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" className={baseClass}>
                <circle cx="12" cy="12" r="10" className="stroke-orange-500" strokeWidth="2" fill="none" />
                <circle cx="12" cy="12" r="5" className="fill-orange-500" />
            </svg>
        );
    }

    if (status === 'completed') {
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" className={baseClass}>
                <circle cx="12" cy="12" r="10" className="stroke-orange-500 fill-orange-500/10 dark:fill-none" strokeWidth="2" fill="none" />
                <path d="M8 12l3 3l5-5" className="stroke-orange-500" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    }

    return (
        <svg width="24" height="24" viewBox="0 0 24 24" className={baseClass}>
            <circle cx="12" cy="12" r="10" className="stroke-stone-300 dark:stroke-stone-700" strokeWidth="2" fill="none" />
        </svg>
    );
};